import React, { useState, useMemo } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { useIsAdminView } from 'hooks';
import { routes } from 'lane-shared/config';
import { useFlag } from 'lane-shared/hooks';
import {
  Table,
  NativeFilterTypes,
  Flex,
  Button,
  FilterType,
  Loading,
} from 'design-system-web';
import { AdminPage } from 'components';
import { Chip, TagCollection, TagListItem } from 'components/ads';
import { H3, H4, M } from 'components/typography';
import { exportCSV } from '../../helpers/exportCSV';
import { TruncateTextComponent } from '../../components/TruncateText/TruncateText';
import { DeleteWatchlistPrompt } from '../../components';
import {
  getWatchlistSeverityTranslation,
  getWatchlistSeverityChipStyle,
} from '../../helpers/watchlistSeverityHelpers';

import { useVisitorWatchlists } from '../../hooks/useVisitorWatchlists';
import { VisitorWatchlist } from 'graphql-query-contracts';
import { convertToUUID, convertTo62 } from 'uuid-encoding';
import { getBuildingTenantChannels } from 'lane-shared/graphql/query';
import { LONG_MONTH_DATE_YEAR } from 'lane-shared/helpers/constants/dates';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { Channel } from 'lane-shared/types/ChannelType';
import { UserType } from 'lane-shared/types/User';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { generateNameVariations, formatFullName, capitalize } from 'formatters';
import { SortDirectionEnum } from 'constants-activate';
import { hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { FeatureFlag } from 'constants-flags';

import styles from './styles.scss';

type ColumnVisibilityState = Record<string, boolean>;

export function VisitorWatchlistPage({
  channel,
  user,
}: {
  channel: Channel;
  user: UserType;
}) {
  const { t } = useTranslation();

  const timeZone = getTimeZoneByGeoLocation({
    latitude: channel?.address?.geo?.[1],
    longitude: channel?.address?.geo?.[0],
  });

  const history = useHistory();
  const [, channelSlug] = useIsAdminView();

  const [visibleColumns, setVisibleColumns] = useState<ColumnVisibilityState>();
  const [activeFilters, setActiveFilters] = useState<ColumnFiltersState>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWatchlist, setSelectedWatchlist] =
    useState<VisitorWatchlist | null>(null);

  const {
    data: watchlistData,
    loading: watchlistLoading,
    error: _watchlistError,
    refetch: refetchWatchlist,
  } = useVisitorWatchlists(convertToUUID(channel._id));

  const refresh = useDebouncedCallback(() => {
    refetchWatchlist();
  }, 500).callback;

  // Check if user has manage permission for watchlist
  const hasManageWatchlistPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE],
      channel._id
    );

  // Check if user has view permission for watchlist
  const hasViewWatchlistPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_VIEW,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE,
      ],
      channel._id
    );

  const columns = useWatchlistColumns(timeZone, channel);
  const rowActions = useRowActions(
    setIsDeleteModalOpen,
    setSelectedWatchlist,
    hasManageWatchlistPermission,
    hasViewWatchlistPermission
  );
  const watchlistTableFilters = useWatchlistFilters();
  const { keywordFilter, searchKeyValue, handleKeywordFilterChange } =
    useKeywordFilter();
  const exportOptions = useExportOptions({
    watchlistData,
    columns,
    activeFilters,
    searchKeyValue,
    visibleColumns,
    channel,
  });

  const onFilterChange = (filters: ColumnFiltersState) => {
    setActiveFilters(filters);
  };

  const onColumnVisibilityChange = (
    columnVisibility: ColumnVisibilityState
  ) => {
    setVisibleColumns(columnVisibility);
  };

  const handleAddToWatchlist = () => {
    if (channelSlug) {
      history.push(
        routes.visitorManagementWatchlistForm.replace(':id', channelSlug)
      );
    }
  };

  return (
    <AdminPage>
      <Flex direction="row" gap={2} align="center" justify="space-between">
        <H3>{t('web.admin.channel.visitor.watchlist.title')}</H3>
        {watchlistData?.length > 0 && (
          <Button
            variant="primary"
            size="large"
            testId="addToWatchlistHeader"
            onClick={handleAddToWatchlist}
            disabled={!hasManageWatchlistPermission}
          >
            {t(
              'web.admin.channel.visitor.watchlist.create.add.watchlist.button'
            )}
          </Button>
        )}
      </Flex>

      {(() => {
        if (watchlistData?.length > 0) {
          return (
            <Table
              columns={columns}
              data={watchlistData}
              pagination="client"
              hasKeywordFilter
              keywordFilterLabel={t(
                'web.admin.channel.visitor.watchlist.search.placeholder'
              )}
              isLoading={watchlistLoading}
              customKeywordFilterFn={keywordFilter}
              onKeywordFilterChange={handleKeywordFilterChange}
              onColumnVisibilityChange={onColumnVisibilityChange}
              rowActions={rowActions}
              onFilterChange={onFilterChange}
              onRefreshClick={refresh}
              filters={watchlistTableFilters}
              activeFilters={activeFilters}
              exportOptions={exportOptions}
              showColumnVisibility
              emptyMessage={t(
                'web.admin.channel.visitor.watchlist.search.empty.message'
              )}
              sorting={{ id: 'updatedAt', direction: SortDirectionEnum.Desc }}
            />
          );
        }

        if (watchlistLoading) {
          return <Loading />;
        }

        return (
          <EmptyState
            onAddClick={handleAddToWatchlist}
            disabled={!hasManageWatchlistPermission}
          />
        );
      })()}

      {selectedWatchlist && (
        <DeleteWatchlistPrompt
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedWatchlist(null);
          }}
          onSuccess={() => {
            refetchWatchlist();
          }}
          watchlistId={selectedWatchlist.id}
          watchlistName={formatFullName(
            capitalize(selectedWatchlist.visitorMatchingInfo.firstName?.primaryValue || ''),
            capitalize(selectedWatchlist.visitorMatchingInfo.lastName?.primaryValue || '')
          )}
        />
      )}
    </AdminPage>
  );
}

const useWatchlistColumns = (timeZone: string, channel: Channel) => {
  const { t } = useTranslation();
  const isRestrictAtTenantEnabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistRestrictAtTenant,
    false
  );
  const isExportChannelNameEnabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistExportChannelName,
    false
  );

  const {
    data: buildingTenants,
  }: {
    data?: { getBuildingTenantChannels: Array<{ channelId: string; name: string }> };
  } = useQuery(getBuildingTenantChannels, {
    variables: { channelId: convertToUUID(channel._id) },
    skip: !isRestrictAtTenantEnabled || !channel?._id,
  });

  // Create a mapping from tenantId (channelId) to tenant name
  // Normalize UUIDs for proper matching
  const tenantIdToNameMap = useMemo(() => {
    if (!buildingTenants?.getBuildingTenantChannels) {
      return new Map<string, string>();
    }

    const map = new Map<string, string>();

    buildingTenants.getBuildingTenantChannels.forEach((tenant) => {
      const normalizedChannelId = convertToUUID(tenant.channelId);

      map.set(normalizedChannelId, tenant.name || '');
    });

    return map;
  }, [buildingTenants]);

  return [
    {
      header: t('web.admin.channel.visitor.watchlist.columns.name'),
      key: 'visitorMatchingInfo.firstName.primaryValue',
      renderForCSV: (_: string, row: VisitorWatchlist) =>
        formatFullName(
          capitalize(row.visitorMatchingInfo.firstName?.primaryValue || ''),
          capitalize(row.visitorMatchingInfo.lastName?.primaryValue || '')
        ),
      renderCell: (_: string, row: VisitorWatchlist) => (
        <Flex direction="column">
          <Flex direction="row">
            <M variant="primary">
              {formatFullName(
                capitalize(row.visitorMatchingInfo.firstName?.primaryValue || ''),
                capitalize(row.visitorMatchingInfo.lastName?.primaryValue || '')
              )}
            </M>
          </Flex>
          <TruncateTextComponent
            text={generateNameVariations({
              primaryFirstName:
                capitalize(row.visitorMatchingInfo.firstName?.primaryValue || ''),
              primaryLastName:
                capitalize(row.visitorMatchingInfo.lastName?.primaryValue || ''),
              firstNameAlternates:
                row.visitorMatchingInfo.firstName?.alternateValues?.map(capitalize) || [],
              lastNameAlternates:
                row.visitorMatchingInfo.lastName?.alternateValues?.map(capitalize) || [],
            })}
            textVariant="secondary"
            maxLength={15}
            tooltipPlacement="bottom"
          />
        </Flex>
      ),
    },
    {
      header: t('web.admin.channel.visitor.watchlist.columns.contact'),
      key: 'visitorMatchingInfo.email.primaryValue',
      renderForCSV: (_: string, row: VisitorWatchlist) => {
        const email = row.visitorMatchingInfo.email?.primaryValue;
        const phone = row.visitorMatchingInfo.phone?.primaryValue;

        if (!email && !phone) {
          return t('web.admin.channel.visitor.watchlist.columns.noContactInfo');
        }

        const contactInfo = [email, phone].filter(Boolean).join(' ');

        return contactInfo;
      },
      renderCell: (_: string, row: VisitorWatchlist) => {
        const hasEmail = !!row.visitorMatchingInfo.email?.primaryValue;
        const hasPhone = !!row.visitorMatchingInfo.phone?.primaryValue;

        if (!hasEmail && !hasPhone) {
          return (
            <M variant="secondary">
              {t('web.admin.channel.visitor.watchlist.columns.noContactInfo')}
            </M>
          );
        }

        return (
          <Flex direction="column">
            {hasEmail && (
              <TruncateTextComponent
                text={row.visitorMatchingInfo.email?.primaryValue || ''}
                textVariant="primary"
                maxLength={15}
                tooltipPlacement="bottom"
              />
            )}
            {hasPhone && (
              <M variant="secondary">
                {row.visitorMatchingInfo.phone?.primaryValue ||
                  'No phone number listed'}
              </M>
            )}
          </Flex>
        );
      },
    },
    ...(isRestrictAtTenantEnabled
      ? [
          {
            header: t('web.admin.channel.visitor.watchlist.columns.restrictEntry'),
            key: 'restrictEntry',
            renderForCSV: (_: string, row: VisitorWatchlist) =>
              isExportChannelNameEnabled
                ? row.tenantIds
                    ?.map(
                      (tenantId) =>
                        tenantIdToNameMap.get(convertToUUID(tenantId)) ||
                        tenantId
                    )
                    .join(',')
                : row.tenantIds?.join(','),
            renderCell: (_: string, row: VisitorWatchlist) => {
              const isPropertyLevel =
                !row.tenantIds ||
                row.tenantIds.length === 0 ||
                row.tenantIds.includes('All tenants');

              if (isPropertyLevel) {
                return (
                  <M variant="primary">
                    {t('web.admin.channel.visitor.watchlist.restrictAt.allTenants')}
                  </M>
                );
              }

              const tenantNames = (row.tenantIds || [])
                .filter((tenantId) => tenantId !== 'All tenants')
                .map((tenantId) => {
                  const normalizedTenantId = convertToUUID(tenantId);

                  return tenantIdToNameMap.get(normalizedTenantId) || tenantId;
                })
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

              // If single tenant, show name directly
              if (tenantNames.length === 1) {
                return (
                  <M variant="primary">
                    {tenantNames[0]}
                  </M>
                );
              }

              // For multiple tenants, show count with tooltip
              const tenantTags: TagListItem[] = tenantNames.map((name) => ({
                label: name,
                value: name,
              }));

              const tooltipContent = (
                <div>
                  <ul className={styles.TenantTooltip}>
                    {tenantNames.map((name) => (
                      <li key={name}>
                        <span>{name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );

              return (
                <TagCollection
                  tags={tenantTags}
                  displayAmount={0}
                  size="sm"
                  doTranslate={false}
                  tooltipPlacement="top"
                  numberedTagLabel="tenants"
                  customNumberedTagTooltip={tooltipContent}
                />
              );
            },
          },
        ]
      : []),
    {
      header: t('web.admin.channel.visitor.watchlist.columns.watchlistLevel'),
      key: 'severityLevel',
      renderForCSV: (_cell: string, row: VisitorWatchlist) =>
        row.watchlistLevelConfig?.displayLevelText ||
        getWatchlistSeverityTranslation(row.severityLevel, t),
      renderCell: (_: string, row: VisitorWatchlist) => (
        <Chip
          value={
            row.watchlistLevelConfig?.displayLevelText ||
            getWatchlistSeverityTranslation(row.severityLevel, t)
          }
          size="sm"
          type={getWatchlistSeverityChipStyle(
            row.watchlistLevelConfig?.level || row.severityLevel
          )}
          testId={`watchlistLevel${row.severityLevel}Chip`}
        />
      ),
    },
    {
      header: t('web.admin.channel.visitor.watchlist.columns.notes'),
      key: 'notes',
      renderForCSV: (cell: string) => cell,
      renderCell: (_: string, row: VisitorWatchlist) => (
        <TruncateTextComponent
          text={row?.notes || ''}
          textVariant="primary"
          maxLength={30}
          tooltipPlacement="bottom"
          textWrap
        />
      ),
    },
    {
      header: t('web.admin.channel.visitor.watchlist.columns.lastUpdated'),
      key: 'updatedAt',
      renderForCSV: (cell: string) => cell,
      renderCell: (_: string, row: VisitorWatchlist) => (
        <M variant="primary">
          {dateFormatter(row.updatedAt, LONG_MONTH_DATE_YEAR, timeZone)}
        </M>
      ),
    },
    {
      header: t('web.admin.channel.visitor.watchlist.columns.reportedBy'),
      key: 'reportedBy',
      renderForCSV: (cell: string) => cell,
      renderCell: (_: string, row: VisitorWatchlist) => (
        <M variant="primary">{row?.reportedBy}</M>
      ),
    },
  ];
};

const useRowActions = (
  setIsDeleteModalOpen: (isOpen: boolean) => void,
  setSelectedWatchlist: (watchlist: VisitorWatchlist | null) => void,
  hasManageWatchlistPermission: boolean = false,
  hasViewWatchlistPermission: boolean = false
) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [, channelSlug] = useIsAdminView();

  return [
    {
      label: t('web.admin.channel.visitor.watchlist.rowActions.edit'),
      onClick: (row: VisitorWatchlist) => {
        if (channelSlug && hasViewWatchlistPermission) {
          history.push(
            routes.visitorManagementWatchlistDetails
              .replace(':id', channelSlug)
              .replace(':watchlistId', convertTo62(row.id))
          );
        }
      },
      isDisabled: !hasViewWatchlistPermission,
    },
    {
      label: t('web.admin.channel.visitor.watchlist.rowActions.remove'),
      onClick: (row: VisitorWatchlist) => {
        if (hasManageWatchlistPermission) {
          setSelectedWatchlist(row);
          setIsDeleteModalOpen(true);
        }
      },
      isDisabled: !hasManageWatchlistPermission,
    },
  ];
};

const createKeywordFilter = (
  row: VisitorWatchlist,
  keyword: string
): boolean => {
  const searchableValues = [
    row.visitorMatchingInfo.firstName?.primaryValue,
    row.visitorMatchingInfo.lastName?.primaryValue,
    row.visitorMatchingInfo.email?.primaryValue,
    row.visitorMatchingInfo.phone?.primaryValue,
    ...(row.visitorMatchingInfo.firstName?.alternateValues || []),
    ...(row.visitorMatchingInfo.lastName?.alternateValues || []),
    ...(row.visitorMatchingInfo.email?.alternateValues || []),
    ...(row.visitorMatchingInfo.phone?.alternateValues || []),
    row.reportedBy,
  ].filter(Boolean); // Remove undefined/null values

  const searchableString = searchableValues.join(' ').toLowerCase();

  return searchableString.includes(keyword.toLowerCase());
};

const useWatchlistFilters = (): FilterType[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'severityLevel',
      type: NativeFilterTypes.Select,
      label: t('web.admin.channel.visitor.watchlist.filters.watchlistLevel'),
      options: [
        {
          label: t('web.admin.channel.visitor.watchlist.levels.high'),
          value: 'WATCHLIST_SEVERITY_LEVEL_HIGH',
        },
      ],
      isPromoted: true,
      isFixed: true,
    },
  ];
};

const useExportOptions = (config: {
  watchlistData: VisitorWatchlist[];
  columns: any[];
  activeFilters: ColumnFiltersState;
  searchKeyValue: string;
  visibleColumns: ColumnVisibilityState | undefined;
  channel: Channel;
}) => {
  const { t } = useTranslation();
  const {
    watchlistData,
    columns,
    activeFilters,
    searchKeyValue,
    visibleColumns,
    channel,
  } = config;

  const WATCHLIST_KEYS_SET = new Set([
    'id',
    'organizationId',
    'severityLevel',
    'notes',
    'reportedBy',
    'createdBy',
    'updatedBy',
    'createdAt',
    'updatedAt',
  ]);

  const isValidWatchlistKey = (key: string): key is keyof VisitorWatchlist =>
    WATCHLIST_KEYS_SET.has(key);

  const filterWatchlistEntries = (
    entries: VisitorWatchlist[],
    filters: ColumnFiltersState
  ) => {
    return entries.filter((entry: VisitorWatchlist) => {
      return filters.every(filter => {
        const { id, value } = filter;

        if (isValidWatchlistKey(id)) {
          if (Array.isArray(value)) {
            return value.includes(entry[id]);
          }

          return entry[id] === value;
        }

        return false;
      });
    });
  };

  const filterData = (data: VisitorWatchlist[], searchKey: string) => {
    return data.filter(row => {
      return createKeywordFilter(row, searchKey);
    });
  };

  const exportCurrentPageAsCSV = () => {
    if (!watchlistData) {
      return;
    }

    const filteredDataWithoutSearch = filterWatchlistEntries(
      watchlistData,
      activeFilters
    );

    const filteredData = filterData(filteredDataWithoutSearch, searchKeyValue);

    const mandatoryKeys = new Set(['name', 'contact', 'severityLevel']);
    const filteredColumn = visibleColumns
      ? columns.filter(
          item => visibleColumns[item.key] || mandatoryKeys.has(item.key)
        )
      : columns;

    exportCSV(
      filteredData,
      filteredColumn,
      `watchlist-current-page-${Date.now()}.csv`,
      channel
    );
  };

  const exportAllAsCSV = () => {
    if (!watchlistData) {
      return;
    }

    exportCSV(
      watchlistData,
      columns,
      `watchlist-all-entries-${Date.now()}.csv`,
      channel
    );
  };

  return [
    {
      label: t('web.admin.visitorManagement.exportSelection'),
      onClick: exportCurrentPageAsCSV,
    },
    {
      label: t('web.admin.visitorManagement.exportAll'),
      onClick: exportAllAsCSV,
    },
  ];
};

const useKeywordFilter = () => {
  const [searchKeyValue, setSearchKeyValue] = useState('');

  const keywordFilter = (row: VisitorWatchlist, keyword: string): boolean => {
    return createKeywordFilter(row, keyword);
  };

  const handleKeywordFilterChange = (keyword: string) => {
    setSearchKeyValue(keyword);
  };

  return { keywordFilter, searchKeyValue, handleKeywordFilterChange };
};

const EmptyState = ({
  onAddClick,
  disabled = false,
}: {
  onAddClick: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={6}
      className={styles.emptyStateContainer}
    >
      <Flex direction="column" gap={4} align="center">
        <H4>{t('web.admin.channel.visitor.watchlist.emptyState.message')}</H4>
        <p>{t('web.admin.channel.visitor.watchlist.emptyState.description')}</p>
      </Flex>
      <Button
        variant="primary"
        size="large"
        testId="addToWatchlistEmptyState"
        onClick={onAddClick}
        disabled={disabled}
      >
        {t('web.admin.channel.visitor.watchlist.create.add.watchlist.button')}
      </Button>
    </Flex>
  );
};
